import {
  type DragEndEvent,
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { flatten, groupBy, isEqual, orderBy } from 'lodash';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { DraggableRow, StyledTable as Table } from './DataTable.style';
import { EmptyState } from './EmptyState';
import { PriorityMoveContext } from './PriorityMoveContext';
import { applyHiddenColumnFilters, customTableRowClassName } from './utils';

import { type DataTableProps, DataTableContext } from './index';

const expandedRowKeyClassNamePrefix = 'expanded-row-id-';

export type MovedRow = {
  key?: React.Key;
  moveCount?: number;
};

/**
 * The row a user just moved with the priority controls. Rows read this themselves so the
 * highlight and scroll stay scoped to this table's rows.
 */
const MovedRowContext = createContext<MovedRow>({});

interface ClientDataTableProps extends Omit<DataTableProps, 'title'> {
  draggable?: boolean;
  onDragUpdate?: (orderedItems: Record<string, any>[], movedItemKey?: React.Key) => void;
  onTableChange?: (pagination: any, filters: any, sorter: any, extra: any) => void;
  isPrioritizing?: boolean;
  handleOrderUpdate?: (orderedItems: Record<string, any>[]) => void;
  movedRow?: MovedRow | null;
}

export const ClientDataTable: React.FunctionComponent<ClientDataTableProps> = ({
  data,
  draggable,
  handleOrderUpdate,
  isPrioritizing,
  loading,
  movedRow,
  onDragUpdate,
  onTableChange,
  rowKey,
  columns,
  filterColumns,
  ...props
}) => {
  const { filteredInfo, multiSortDef, updatePagination, initialPagination } =
    useContext(DataTableContext) || {};
  const [draggingId, setDraggingId] = useState<string>();
  const columnsForFilters = filterColumns ?? columns;

  /**
   * Some columns control filters for multiple properties, e.g. damage severity and critical
   * status. See ./README for detail.
   *
   * In these cases, the column definition's `onFilter` cannot be applied directly. The filters
   * are grouped by property here, and their filter functions are pulled out of the column
   * definition's `onFilter` or `additionalFilterFunctions`, and applied to the data before it is
   * passed to the table component.
   */
  const applyMultiFilters = useCallback(
    (data: Record<string, any>[]): Record<string, any>[] => {
      const columnMultiFilters = flatten(
        Object.values(filteredInfo ?? {}).filter((values: any[]) =>
          Object.hasOwn(values?.[0] ?? {}, 'key')
        )
      );
      const groupedMultiFilters = Object.entries(groupBy(columnMultiFilters, 'key')).map(
        ([k, v]) => [k, v.map(({ value }) => value)]
      );

      if (groupedMultiFilters.length) {
        return (data ?? []).reduce((filteredRows: Record<string, any>[], row) => {
          const filterResults: boolean[] = groupedMultiFilters.map(
            ([k, values]: [string, any[]]) => {
              const basePropertyFilterFunction = columnsForFilters?.find(
                ({ key }) => key === k
              )?.onFilter;
              const secondaryPropertyFilterFunction = columnsForFilters?.find(
                ({ additionalFilterFunctions }) => !!additionalFilterFunctions?.[k]
              )?.additionalFilterFunctions?.[k];

              if (basePropertyFilterFunction) {
                return values.map((v) => basePropertyFilterFunction(v, row)).every((r) => !!r);
              } else if (secondaryPropertyFilterFunction) {
                return values.map((v) => secondaryPropertyFilterFunction(v, row)).every((r) => !!r);
              } else {
                return true;
              }
            }
          );

          if (filterResults.every((r) => !!r)) {
            return [...(filteredRows ?? []), row];
          }
          return filteredRows;
        }, []) as Record<string, any>[];
      }

      return data;
    },
    [columnsForFilters, filteredInfo]
  );
  const applyMultiSort = useCallback(
    (data: Record<string, any>[]): Record<string, any>[] => {
      // If there is a multiSortDef, we need to apply that mutli-column sort manually.
      const sortedData = orderBy(
        data,
        multiSortDef?.map(({ column: { get } }) => get),
        // sortDirection is by default in all caps for server-side functionality.
        // but lodash expects lowercase here.
        multiSortDef?.map(({ sortDirection }) => sortDirection.toLowerCase())
      );
      // Check to see if the contents of dataSource changed after sorting.
      const sourcesEqual = isEqual(data, sortedData);

      // If prioritizing, we need to trigger a specific callback to update the priority state
      // It's only safe to call the prioritization handleUpdate callback if we are prioritizing
      // and we have the callback
      // and the and the sort has changed the order of the contents of the list
      // and we're not currently updating using drag and drop
      if (isPrioritizing && handleOrderUpdate && !sourcesEqual) {
        handleOrderUpdate(sortedData);
      }
      return sortedData;
    },
    [handleOrderUpdate, isPrioritizing, multiSortDef]
  );

  const dataSource = useMemo(() => {
    // for most implementations, we want to remove null values.
    // Implementations that do not want this can replace a null value with empty object.
    const baseData = applyHiddenColumnFilters((data || []).filter(Boolean), columnsForFilters);
    const filteredData = applyMultiFilters(baseData);

    if (multiSortDef) {
      return applyMultiSort(filteredData);
    }

    return filteredData;
  }, [columnsForFilters, data, multiSortDef, applyMultiFilters, applyMultiSort]);

  const draggingRow = useMemo(
    () => draggingId && dataSource.find((row) => row.id === draggingId),
    [draggingId, dataSource]
  );

  const movedRowValue = useMemo(
    () => ({ key: movedRow?.key, moveCount: movedRow?.moveCount }),
    [movedRow?.key, movedRow?.moveCount]
  );

  useEffect(
    () => {
      if (draggable) {
        updatePagination(false);
      } else {
        updatePagination(initialPagination);
      }
    },
    //intentionally omitting update/initialPagination
    [draggable] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Configuration for drag event callbacks and styling
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    })
  );

  /**
   * Both drag and arrow controls move the same records within the same `dataSource`. Keys read
   * from the DOM are strings, while record ids may be numbers, so comparisons are normalized.
   */
  const handleMovePriorityItem = useCallback(
    (activeKey: React.Key, overKey: React.Key, highlightMovedItem = false) => {
      if (!dataSource || !onDragUpdate || String(activeKey) === String(overKey)) {
        return;
      }

      const findRowIndex = (key: React.Key) =>
        dataSource.findIndex((item) => {
          const itemKey = item.key ?? item.id;
          return [String(itemKey), `${expandedRowKeyClassNamePrefix}${itemKey}`].includes(
            String(key)
          );
        });
      const activeIndex = findRowIndex(activeKey);
      const overIndex = findRowIndex(overKey);

      if (activeIndex < 0 || overIndex < 0) {
        return;
      }

      const movedItemKey = dataSource[activeIndex].key ?? dataSource[activeIndex].id;
      onDragUpdate(
        arrayMove(dataSource, activeIndex, overIndex),
        highlightMovedItem ? movedItemKey : undefined
      );
    },
    [dataSource, onDragUpdate]
  );

  const handleArrowMove = useCallback(
    (activeKey: React.Key, overKey: React.Key) => handleMovePriorityItem(activeKey, overKey, true),
    [handleMovePriorityItem]
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (over) {
      handleMovePriorityItem(active.id, over.id);
    }
    setDraggingId(undefined);
  };

  const handlePrioritizingTableChange = (
    pagination: any,
    filters: any,
    sorter: any,
    extra: any
  ) => {
    onTableChange?.(pagination, filters, sorter, extra);
    // If the table is prioritizing and the table changes, we need to pass the updated ordering of the table to the priority-handling
    // handleOrderUpdate function, so that the save button can be correctly enabled/disabled.

    if (isPrioritizing && handleOrderUpdate) {
      const orderedItems = extra.currentDataSource;
      handleOrderUpdate(orderedItems);
    }
  };

  return (
    <MovedRowContext.Provider value={movedRowValue}>
      <PriorityMoveContext.Provider value={handleArrowMove}>
        <DndContext
          sensors={sensors}
          modifiers={[restrictToVerticalAxis]}
          onDragStart={(event) => setDraggingId(event.active.id)}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={(dataSource ?? []).map((i) => i.id)}
            strategy={verticalListSortingStrategy}
            disabled={!isPrioritizing}
          >
            <Table
              style={draggingId ? { overflowX: 'hidden' } : {}}
              locale={{ emptyText: <EmptyState {...props} loading={!!loading} /> }}
              {...props}
              columns={columns}
              components={{
                body: { row: Row },
              }}
              expandable={{
                ...props.expandable,
                /**
                 * add a class containing the parent row's key to the expanded row, which can
                 * be used to identify it during drag & drop events
                 */
                expandedRowClassName: (record: { key?: string; id?: string }) =>
                  `${expandedRowKeyClassNamePrefix}${record.key ?? record.id}`,
              }}
              loading={loading}
              dataSource={dataSource}
              bordered={false}
              rowKey={rowKey}
              onChange={(...args) =>
                isPrioritizing ? handlePrioritizingTableChange(...args) : onTableChange?.(...args)
              }
              rowClassName={customTableRowClassName}
            />
            <DragOverlay>
              {draggingRow ? (
                <Table
                  $draggingTable={true}
                  {...props}
                  expandable={{
                    ...props.expandable,
                    expandedRowKeys: [],
                  }}
                  columns={columns}
                  dataSource={[draggingRow]}
                  rowKey={rowKey}
                  bordered={false}
                />
              ) : null}
            </DragOverlay>
          </SortableContext>
        </DndContext>
      </PriorityMoveContext.Provider>
    </MovedRowContext.Provider>
  );
};

const prefersReducedMotion = () =>
  !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * Find the expanded row key classname, if it exists
 */
const getExpandedRowKeyClassName = (className: string): string | undefined => {
  const idClassName = className.split(' ').find((c) => c.startsWith(expandedRowKeyClassNamePrefix));
  return idClassName;
};

const Row: React.FC<
  React.HTMLAttributes<HTMLTableRowElement> & {
    'data-row-key'?: string;
  }
> = (props) => {
  /**
   * sortableId is used in drag & drop logic for styling/transitions as well as in event handlers
   * (e.g. `handleDragEnd` in ClientDataTable). For normal rows, this is just the `data-row-key`, which
   * is set by the table component internally. For expanded rows, we set it to the `expandedRowClassName`
   * (including the prefix), so that it is identifiable in handlers, but unique from the parent row's
   * `sortableId` to avoid styling conflicts.
   */
  const sortableId: string | undefined =
    props['data-row-key'] ?? getExpandedRowKeyClassName(props['className'] ?? '');
  const { attributes, listeners, setNodeRef, transform } = useSortable({
    id: sortableId ?? '',
  });

  const isDraggable = !attributes['aria-disabled'];

  const { key: movedRowKey, moveCount } = useContext(MovedRowContext);
  const isMovedRow = !!sortableId && !!movedRowKey && String(sortableId) === String(movedRowKey);
  const rowRef = useRef<HTMLTableRowElement | null>(null);

  /**
   * The ref callback has to keep the same identity across renders. An inline one would
   * detach and re-attach dnd-kit's node ref on every render, which loops through its
   * internal state updates and locks up the table.
   */
  const latestSetNodeRef = useRef(setNodeRef);
  latestSetNodeRef.current = setNodeRef;
  const setRefs = useCallback((node: HTMLTableRowElement | null) => {
    latestSetNodeRef.current(node);
    rowRef.current = node;
  }, []);

  useEffect(() => {
    if (isMovedRow) {
      // Optional call: jsdom does not implement scrollIntoView.
      rowRef.current?.scrollIntoView?.({
        block: 'nearest',
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });
    }
    // moveCount re-runs this when the same row is moved again
  }, [isMovedRow, moveCount]);

  return (
    <DraggableRow
      {...props}
      className={[props.className, isMovedRow ? 'priority-moved-row' : '']
        .filter(Boolean)
        .join(' ')}
      ref={setRefs}
      $isDraggable={isDraggable}
      $transform={CSS.Translate.toString(transform)}
      /**
       * Only apply dnd-kit's sortable attributes/listeners when the row is actually
       * draggable. When the SortableContext is disabled (non-prioritizing tables, i.e. the
       * vast majority), dnd-kit marks every row `role="button" aria-disabled="true"`, which
       * makes controls inside the row (checkboxes, expand icons, edit buttons) read as
       * disabled to assistive tech and test tooling.
       */
      {...(isDraggable ? attributes : {})}
      {...(isDraggable ? listeners : {})}
    />
  );
};
